import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { API_URL } from '@/utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DownloadButton } from '@/utils/DownloadButton';
import {
  Banknote, Calculator, Users, Download, FileSpreadsheet, RefreshCw,
  Loader2, Eye, Shield, TrendingUp, Percent, ChevronRight, Save,
  FileText, Search, Scale
} from 'lucide-react';

const API = API_URL;
const fmt = (n) => n != null ? Number(n).toLocaleString('fr-FR') : '0';

/* ===== KPI CARD ===== */
function KpiCard({ icon: Icon, label, value, unit, color }) {
  return (
    <Card className="border-slate-200 dark:border-slate-700" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
          <Icon size={18} className="text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{label}</p>
          <p className="text-lg font-bold text-slate-900 dark:text-white">{value} <span className="text-xs font-normal text-slate-400">{unit}</span></p>
        </div>
      </CardContent>
    </Card>
  );
}

/* ===== DETAIL SHEET ===== */
function BulletinSheet({ bulletin, open, onClose }) {
  if (!bulletin) return null;
  const c = bulletin.calcul;
  const Line = ({ label, val, sub, bold, color }) => (
    <div className={`flex justify-between py-1.5 ${bold ? 'font-semibold' : ''} ${color || ''}`}>
      <span className="text-sm text-slate-600 dark:text-slate-400">{label}</span>
      <span className={`text-sm ${bold ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
        {val} {sub && <span className="text-xs text-slate-400">{sub}</span>}
      </span>
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="bulletin-detail-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FileText size={18} className="text-primary" />
            Bulletin de paie
          </SheetTitle>
          <SheetDescription>
            {bulletin.employee_name} ({bulletin.employee_code}) - {bulletin.period_label}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-5">
          {/* Brut + Heures */}
          <div>
            <h4 className="text-xs font-bold uppercase text-slate-400 mb-2 tracking-wider">Remuneration</h4>
            <Line label="Salaire brut" val={`${fmt(c.salaire_brut)} XOF`} bold />
            <Line label="Taux horaire" val={`${fmt(c.taux_horaire)} XOF/h`} />
            <Line label="Heures regulieres" val={`${c.regular_hours}h`} />
            <Line label="Heures supp. (41-48h, +15%)" val={`${c.overtime.heures_41_48}h = ${fmt(c.overtime.montant_15pct)} XOF`} />
            <Line label="Heures supp. (>48h, +40%)" val={`${c.overtime.heures_sup_48}h = ${fmt(c.overtime.montant_40pct)} XOF`} />
            <Line label="Total heures supp." val={`${fmt(c.overtime.total)} XOF`} bold />
          </div>
          <Separator />
          {/* Retenues salariales */}
          <div>
            <h4 className="text-xs font-bold uppercase text-slate-400 mb-2 tracking-wider">Retenues salariales</h4>
            <Line label="IPRES General" val={`-${fmt(c.ipres.regime_general.salarie)} XOF`} sub={`base ${fmt(c.ipres.regime_general.base)}`} />
            {c.ipres.regime_cadre && <Line label="IPRES Cadre" val={`-${fmt(c.ipres.regime_cadre.salarie)} XOF`} sub={`base ${fmt(c.ipres.regime_cadre.base)}`} />}
            <Line label="CSS Maladie" val={`-${fmt(c.css.maladie_salarie)} XOF`} sub={`base ${fmt(c.css.base)}`} />
            <Line label="TRIMF" val={`-${fmt(c.trimf.mensuel)} XOF`} sub={`annuel ${fmt(c.trimf.annuel)}`} />
            <Line label="IRPP" val={`-${fmt(c.irpp.mensuel)} XOF`} sub={`${c.irpp.parts} part(s)`} />
            <Line label="Total retenues" val={`-${fmt(c.total_retenues_salariales)} XOF`} bold color="text-red-600 dark:text-red-400" />
          </div>
          <Separator />
          {/* Charges patronales */}
          <div>
            <h4 className="text-xs font-bold uppercase text-slate-400 mb-2 tracking-wider">Charges patronales</h4>
            <Line label="IPRES General" val={`${fmt(c.ipres.regime_general.patronal)} XOF`} />
            {c.ipres.regime_cadre && <Line label="IPRES Cadre" val={`${fmt(c.ipres.regime_cadre.patronal)} XOF`} />}
            <Line label="CSS Prest. familiales" val={`${fmt(c.css.prestations_familiales_patronal)} XOF`} />
            <Line label="CSS Accident travail" val={`${fmt(c.css.accident_travail_patronal)} XOF`} />
            <Line label="Total charges" val={`${fmt(c.total_charges_patronales)} XOF`} bold />
          </div>
          <Separator />
          {/* Net */}
          <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-4 space-y-1">
            <Line label="Prime transport" val={`+${fmt(c.prime_transport)} XOF`} />
            <div className="flex justify-between pt-2">
              <span className="text-base font-bold text-slate-900 dark:text-white">Net a payer</span>
              <span className="text-base font-bold text-emerald-600 dark:text-emerald-400">{fmt(c.net_a_payer)} XOF</span>
            </div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-4">
            <div className="flex justify-between">
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Cout total employeur</span>
              <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{fmt(c.cout_total_employeur)} XOF</span>
            </div>
          </div>
          {/* Download PDF */}
          <DownloadButton url={`${API}/paierh/bulletin/${bulletin.id}/pdf`} className="w-full gap-2" data-testid="download-bulletin-pdf">
            <Download size={14} /> Telecharger le bulletin PDF
          </DownloadButton>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ===== MAIN PAGE ===== */
export default function PaieRHPage() {
  const { token } = useAuth();
  const { effectiveTenantId } = useTenant();
  const { language } = useLanguage();
  const fr = language === 'fr';

  const [activeTab, setActiveTab] = useState('config');
  const [employees, setEmployees] = useState([]);
  const [bulletins, setBulletins] = useState([]);
  const [summary, setSummary] = useState(null);
  const [constantes, setConstantes] = useState(null);
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBulletin, setSelectedBulletin] = useState(null);
  const [editingConfig, setEditingConfig] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  const fetchConfig = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const res = await axios.get(`${API}/paierh/config?tenantId=${effectiveTenantId}`, { headers });
      setEmployees(res.data);
    } catch (err) { console.error(err); }
  }, [effectiveTenantId, token]);

  const fetchBulletins = useCallback(async () => {
    if (!effectiveTenantId || !selectedPeriod) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/paierh/bulletins?tenantId=${effectiveTenantId}&period_id=${selectedPeriod}`, { headers });
      setBulletins(res.data.bulletins);
      setSummary(res.data.summary);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [effectiveTenantId, selectedPeriod, token]);

  const fetchPeriods = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const res = await axios.get(`${API}/payroll/periods?tenantId=${effectiveTenantId}`, { headers });
      setPayPeriods(res.data);
      if (res.data.length > 0 && !selectedPeriod) {
        setSelectedPeriod(res.data[0].id);
      }
    } catch (err) { console.error(err); }
  }, [effectiveTenantId, token]);

  const fetchConstantes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/paierh/constantes`, { headers });
      setConstantes(res.data);
    } catch (err) { console.error(err); }
  }, [token]);

  useEffect(() => { fetchConfig(); fetchPeriods(); fetchConstantes(); }, [fetchConfig, fetchPeriods, fetchConstantes]);
  useEffect(() => { if (selectedPeriod) fetchBulletins(); }, [fetchBulletins, selectedPeriod]);

  const handleSaveConfig = async (emp) => {
    const cfg = editingConfig[emp.employee_id] || {};
    try {
      await axios.post(`${API}/paierh/config?tenantId=${effectiveTenantId}`, {
        employee_id: emp.employee_id,
        salaire_brut: cfg.salaire_brut ?? emp.salaire_brut,
        regime_ipres: cfg.regime_ipres ?? emp.regime_ipres,
        parts_irpp: cfg.parts_irpp ?? emp.parts_irpp,
        prime_transport: cfg.prime_transport ?? emp.prime_transport,
      }, { headers });
      toast.success(`Configuration sauvegardee pour ${emp.firstName} ${emp.lastName}`);
      setEditingConfig(prev => { const n = { ...prev }; delete n[emp.employee_id]; return n; });
      fetchConfig();
    } catch (err) {
      toast.error('Erreur de sauvegarde');
    }
  };

  const handleCalculer = async () => {
    if (!selectedPeriod) return toast.error('Selectionnez une periode');
    setCalculating(true);
    try {
      const res = await axios.post(`${API}/paierh/calculer`, {
        tenantId: effectiveTenantId,
        period_id: selectedPeriod,
      }, { headers });
      toast.success(`${res.data.count} bulletin(s) calcule(s)`);
      fetchBulletins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de calcul');
    } finally { setCalculating(false); }
  };

  const updateField = (empId, field, value) => {
    setEditingConfig(prev => ({
      ...prev,
      [empId]: { ...(prev[empId] || {}), [field]: value }
    }));
  };

  const getVal = (emp, field) => {
    return editingConfig[emp.employee_id]?.[field] ?? emp[field];
  };

  const filteredEmployees = employees.filter(e =>
    `${e.code} ${e.firstName} ${e.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredBulletins = bulletins.filter(b =>
    `${b.employee_code} ${b.employee_name}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="paierh-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Banknote size={24} className="text-emerald-500" /> PaieRH Africa
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Calcul de paie senegalais (IPRES, CSS, IRPP, TRIMF)</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md" data-testid="paierh-tabs">
          <TabsTrigger value="config" className="gap-1.5 text-xs" data-testid="tab-config">
            <Users size={14} /> Configuration
          </TabsTrigger>
          <TabsTrigger value="bulletins" className="gap-1.5 text-xs" data-testid="tab-bulletins">
            <Calculator size={14} /> Bulletins
          </TabsTrigger>
          <TabsTrigger value="taux" className="gap-1.5 text-xs" data-testid="tab-taux">
            <Scale size={14} /> Taux legaux
          </TabsTrigger>
        </TabsList>

        {/* ===== TAB: CONFIGURATION SALAIRES ===== */}
        <TabsContent value="config" className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Rechercher..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-sm" data-testid="config-search" />
            </div>
            <Badge variant="secondary" className="text-xs">{employees.length} employes</Badge>
          </div>

          <Card className="border-slate-200 dark:border-slate-700">
            <ScrollArea className="max-h-[60vh]">
              <Table data-testid="config-table">
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                    <TableHead className="text-xs w-16">Code</TableHead>
                    <TableHead className="text-xs">Nom</TableHead>
                    <TableHead className="text-xs">Poste</TableHead>
                    <TableHead className="text-xs w-32">Brut (XOF)</TableHead>
                    <TableHead className="text-xs w-28">Regime IPRES</TableHead>
                    <TableHead className="text-xs w-20">Parts IRPP</TableHead>
                    <TableHead className="text-xs w-28">Transport (XOF)</TableHead>
                    <TableHead className="text-xs w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.map(emp => {
                    const hasChanges = !!editingConfig[emp.employee_id];
                    return (
                      <TableRow key={emp.employee_id} className={hasChanges ? 'bg-amber-50/50 dark:bg-amber-950/10' : ''}>
                        <TableCell className="text-xs font-mono">{emp.code}</TableCell>
                        <TableCell className="text-sm font-medium">{emp.firstName} {emp.lastName}</TableCell>
                        <TableCell className="text-xs text-slate-500">{emp.position || '-'}</TableCell>
                        <TableCell>
                          <Input type="number" value={getVal(emp, 'salaire_brut')}
                            onChange={e => updateField(emp.employee_id, 'salaire_brut', Number(e.target.value))}
                            className="h-7 text-xs w-28" data-testid={`brut-${emp.code}`} />
                        </TableCell>
                        <TableCell>
                          <Select value={getVal(emp, 'regime_ipres')} onValueChange={v => updateField(emp.employee_id, 'regime_ipres', v)}>
                            <SelectTrigger className="h-7 text-xs w-24" data-testid={`regime-${emp.code}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general">General</SelectItem>
                              <SelectItem value="cadre">Cadre</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input type="number" step="0.5" min="1" value={getVal(emp, 'parts_irpp')}
                            onChange={e => updateField(emp.employee_id, 'parts_irpp', Number(e.target.value))}
                            className="h-7 text-xs w-16" data-testid={`parts-${emp.code}`} />
                        </TableCell>
                        <TableCell>
                          <Input type="number" value={getVal(emp, 'prime_transport')}
                            onChange={e => updateField(emp.employee_id, 'prime_transport', Number(e.target.value))}
                            className="h-7 text-xs w-24" data-testid={`transport-${emp.code}`} />
                        </TableCell>
                        <TableCell>
                          {hasChanges && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleSaveConfig(emp)}
                              data-testid={`save-${emp.code}`}>
                              <Save size={14} className="text-primary" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          </Card>
        </TabsContent>

        {/* ===== TAB: BULLETINS ===== */}
        <TabsContent value="bulletins" className="space-y-4">
          {/* Period selector + actions */}
          <div className="flex flex-wrap items-center gap-2">
            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
              <SelectTrigger className="h-8 w-64 text-xs" data-testid="period-select">
                <SelectValue placeholder="Selectionnez une periode" />
              </SelectTrigger>
              <SelectContent>
                {payPeriods.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.startDate} - {p.endDate} ({p.status})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={handleCalculer} disabled={calculating || !selectedPeriod}
              data-testid="calculer-btn">
              {calculating ? <Loader2 size={14} className="animate-spin" /> : <Calculator size={14} />}
              Calculer la paie
            </Button>
            {bulletins.length > 0 && (
              <>
                <DownloadButton url={`${API}/paierh/export/masse-salariale?tenantId=${effectiveTenantId}&period_id=${selectedPeriod}&format=csv`}
                  className="h-8 gap-1 text-xs" size="sm" data-testid="export-csv">
                  <FileSpreadsheet size={14} /> CSV
                </DownloadButton>
                <DownloadButton url={`${API}/paierh/export/masse-salariale?tenantId=${effectiveTenantId}&period_id=${selectedPeriod}&format=wave`}
                  className="h-8 gap-1 text-xs" size="sm" data-testid="export-wave">
                  <Download size={14} /> Wave
                </DownloadButton>
              </>
            )}
          </div>

          {/* KPIs */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bulletins-kpis">
              <KpiCard icon={Users} label="Employes" value={summary.count} unit="" color="bg-blue-500" />
              <KpiCard icon={Banknote} label="Masse brute" value={fmt(summary.masse_salariale_brute)} unit="XOF" color="bg-slate-600" />
              <KpiCard icon={TrendingUp} label="Masse nette" value={fmt(summary.masse_salariale_nette)} unit="XOF" color="bg-emerald-500" />
              <KpiCard icon={Shield} label="Cout employeur" value={fmt(summary.cout_total_employeur)} unit="XOF" color="bg-purple-500" />
            </div>
          )}

          {/* Search */}
          <div className="relative max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Rechercher..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-sm" data-testid="bulletins-search" />
          </div>

          {/* Table */}
          <Card className="border-slate-200 dark:border-slate-700">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="animate-spin text-primary" size={24} />
              </div>
            ) : bulletins.length === 0 ? (
              <div className="text-center py-16 text-sm text-slate-400">
                {selectedPeriod ? 'Aucun bulletin. Cliquez "Calculer la paie" pour generer les bulletins.' : 'Selectionnez une periode de paie.'}
              </div>
            ) : (
              <ScrollArea className="max-h-[55vh]">
                <Table data-testid="bulletins-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                      <TableHead className="text-xs">Code</TableHead>
                      <TableHead className="text-xs">Nom</TableHead>
                      <TableHead className="text-xs">Poste</TableHead>
                      <TableHead className="text-xs text-right">Brut</TableHead>
                      <TableHead className="text-xs text-right">Retenues</TableHead>
                      <TableHead className="text-xs text-right">HS</TableHead>
                      <TableHead className="text-xs text-right">Net a payer</TableHead>
                      <TableHead className="text-xs text-right">Cout empl.</TableHead>
                      <TableHead className="text-xs w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBulletins.map(b => (
                      <TableRow key={b.id} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/30"
                        onClick={() => setSelectedBulletin(b)} data-testid={`bulletin-row-${b.employee_code}`}>
                        <TableCell className="text-xs font-mono">{b.employee_code}</TableCell>
                        <TableCell className="text-sm font-medium">{b.employee_name}</TableCell>
                        <TableCell className="text-xs text-slate-500">{b.employee_position || '-'}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{fmt(b.calcul.salaire_brut)}</TableCell>
                        <TableCell className="text-xs text-right font-mono text-red-500">-{fmt(b.calcul.total_retenues_salariales)}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{fmt(b.calcul.overtime.total)}</TableCell>
                        <TableCell className="text-xs text-right font-mono font-bold text-emerald-600">{fmt(b.calcul.net_a_payer)}</TableCell>
                        <TableCell className="text-xs text-right font-mono text-blue-600">{fmt(b.calcul.cout_total_employeur)}</TableCell>
                        <TableCell><Eye size={14} className="text-slate-400" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </Card>
        </TabsContent>

        {/* ===== TAB: TAUX LEGAUX ===== */}
        <TabsContent value="taux" className="space-y-4">
          {!constantes ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-primary" size={24} /></div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* SMIG */}
              <Card data-testid="taux-smig">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><Banknote size={16} className="text-emerald-500" /> SMIG</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-emerald-600">{fmt(constantes.smig)} <span className="text-sm font-normal text-slate-400">XOF / mois</span></p>
                  <p className="text-xs text-slate-400 mt-1">Salaire Minimum Interprofessionnel Garanti</p>
                </CardContent>
              </Card>

              {/* IPRES */}
              <Card data-testid="taux-ipres">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><Shield size={16} className="text-blue-500" /> IPRES</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Badge variant="outline" className="text-xs mb-1">Regime General</Badge>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div><span className="text-slate-400">Plafond</span><p className="font-mono font-bold">{fmt(constantes.ipres_general.plafond)}</p></div>
                      <div><span className="text-slate-400">Salarie</span><p className="font-mono font-bold text-red-500">{(constantes.ipres_general.taux_salarie * 100).toFixed(1)}%</p></div>
                      <div><span className="text-slate-400">Patronal</span><p className="font-mono font-bold text-blue-500">{(constantes.ipres_general.taux_patronal * 100).toFixed(1)}%</p></div>
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <Badge variant="outline" className="text-xs mb-1">Regime Cadre</Badge>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div><span className="text-slate-400">Plafond</span><p className="font-mono font-bold">{fmt(constantes.ipres_cadre.plafond)}</p></div>
                      <div><span className="text-slate-400">Salarie</span><p className="font-mono font-bold text-red-500">{(constantes.ipres_cadre.taux_salarie * 100).toFixed(1)}%</p></div>
                      <div><span className="text-slate-400">Patronal</span><p className="font-mono font-bold text-blue-500">{(constantes.ipres_cadre.taux_patronal * 100).toFixed(1)}%</p></div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* CSS */}
              <Card data-testid="taux-css">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><Percent size={16} className="text-amber-500" /> CSS</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {[
                    { label: 'Prestations familiales', taux: `${(constantes.css.prestations_familiales.taux_patronal * 100).toFixed(1)}% patronal`, plafond: constantes.css.prestations_familiales.plafond },
                    { label: 'Accident travail', taux: `${(constantes.css.accident_travail.taux_patronal * 100).toFixed(1)}% patronal`, plafond: constantes.css.accident_travail.plafond },
                    { label: 'Maladie', taux: `${(constantes.css.maladie.taux_salarie * 100).toFixed(1)}% salarie`, plafond: constantes.css.maladie.plafond },
                  ].map(item => (
                    <div key={item.label} className="flex justify-between items-center text-xs py-1">
                      <span className="text-slate-600 dark:text-slate-400">{item.label}</span>
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary" className="text-xs font-mono">{item.taux}</Badge>
                        <span className="text-slate-400 font-mono">plaf. {fmt(item.plafond)}</span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* TRIMF */}
              <Card data-testid="taux-trimf">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><Scale size={16} className="text-purple-500" /> TRIMF (annuel)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Tranche annuelle (XOF)</TableHead>
                        <TableHead className="text-xs text-right">Montant</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {constantes.trimf_tranches.map((t, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs font-mono">{fmt(t.min)} - {t.max ? fmt(t.max) : '+'}</TableCell>
                          <TableCell className="text-xs text-right font-mono font-bold">{fmt(t.montant)} XOF</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* IRPP */}
              <Card className="lg:col-span-2" data-testid="taux-irpp">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><TrendingUp size={16} className="text-red-500" /> IRPP (bareme progressif annuel)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Tranche annuelle (XOF)</TableHead>
                        <TableHead className="text-xs text-right">Taux</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {constantes.irpp_tranches.map((t, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs font-mono">{fmt(t.min)} - {t.max ? fmt(t.max) : '+'}</TableCell>
                          <TableCell className="text-xs text-right">
                            <Badge variant={t.taux === 0 ? 'secondary' : 'default'} className="text-xs font-mono">
                              {(t.taux * 100).toFixed(0)}%
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Heures supp */}
              <Card className="lg:col-span-2" data-testid="taux-heures-sup">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><RefreshCw size={16} className="text-orange-500" /> Heures supplementaires</CardTitle>
                </CardHeader>
                <CardContent className="flex gap-8">
                  <div>
                    <p className="text-xs text-slate-400">Heures legales / semaine</p>
                    <p className="text-lg font-bold">{constantes.heures_legales_semaine}h</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">41h - 48h</p>
                    <p className="text-lg font-bold text-amber-500">+{(constantes.overtime_rate_41_48 * 100).toFixed(0)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Au-dela de 48h</p>
                    <p className="text-lg font-bold text-red-500">+{(constantes.overtime_rate_above_48 * 100).toFixed(0)}%</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Bulletin Detail Sheet */}
      <BulletinSheet bulletin={selectedBulletin} open={!!selectedBulletin} onClose={() => setSelectedBulletin(null)} />
    </div>
  );
}
